// P13 — Seção "Cotas de IA" integrada ao /admin/usuarios/<id>/editar.
//
// 2 abas: "Editar" mostra contadores + sliders/inputs de limites + multiselect
// de modelos + reset emergencial. "Auditoria" lista o histórico (UserQuotaChangeLog).
// O `reason` opcional no payload é propagado pro signal pre_save → audit log.

import { FC, useCallback, useEffect, useState } from 'react';
import adminAgentsService from '@/services/adminAgents.service';
import { useToast } from '@/contexts/ToastContext';
import {
  AdminUserQuota,
  UpdateUserQuotaPayload,
  UserQuotaChangeLog,
} from '@/types/agents';

type TabId = 'edit' | 'audit';

function fmtDate(s: string): string {
  return new Date(s).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

function fmtPct(used: number, max: number): string {
  if (!max) return '0%';
  return `${Math.round((used / max) * 100)}%`;
}

function UsageBar({ label, used, max }: { label: string; used: number; max: number }) {
  const pct = max ? Math.min(100, (used / max) * 100) : 0;
  const danger = pct >= 90;
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs">
        <span className="text-content-tertiary">{label}</span>
        <span className={`font-mono ${danger ? 'text-red-300' : 'text-content-primary'}`}>
          {used.toLocaleString('pt-BR')} / {max.toLocaleString('pt-BR')} ({fmtPct(used, max)})
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-surface-border overflow-hidden">
        <div
          className={`h-full transition-all ${danger ? 'bg-red-400' : 'bg-accent'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function NumberInput({
  label,
  value,
  onChange,
  testId,
}: {
  label: string;
  value: number | undefined;
  onChange: (v: number) => void;
  testId: string;
}) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-content-primary block mb-1">{label}</span>
      <input
        type="number"
        min={0}
        value={value ?? 0}
        onChange={(e) => onChange(Math.max(0, Number(e.target.value)))}
        data-testid={testId}
        className="w-full rounded-lg bg-surface-secondary border border-surface-border px-3 py-2 text-content-primary text-sm"
      />
    </label>
  );
}

interface Props {
  userId: string;
}

const UserQuotaSection: FC<Props> = ({ userId }) => {
  const { showToast } = useToast();
  const [quota, setQuota] = useState<AdminUserQuota | null>(null);
  const [form, setForm] = useState<Partial<AdminUserQuota> | null>(null);
  const [reason, setReason] = useState('');
  const [audit, setAudit] = useState<UserQuotaChangeLog[]>([]);
  const [tab, setTab] = useState<TabId>('edit');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [q, a] = await Promise.all([
        adminAgentsService.getUserQuota(userId),
        adminAgentsService.getUserQuotaAudit(userId).catch(() => []),
      ]);
      setQuota(q);
      setForm(q);
      setAudit(a);
    } catch {
      showToast('Não foi possível carregar a cota.', 'error');
    } finally {
      setLoading(false);
    }
  }, [userId, showToast]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const handleSave = async () => {
    if (!form) return;
    setSaving(true);
    try {
      const payload: UpdateUserQuotaPayload = {
        max_personal_agents: form.max_personal_agents,
        max_files_per_agent: form.max_files_per_agent,
        max_file_size_mb: form.max_file_size_mb,
        max_tokens_per_day: form.max_tokens_per_day,
        max_tokens_per_month: form.max_tokens_per_month,
        allowed_models: form.allowed_models,
        ...(reason ? { reason } : {}),
      };
      await adminAgentsService.updateUserQuota(userId, payload);
      showToast('Cota atualizada.', 'success');
      setReason('');
      await refresh();
    } catch {
      showToast('Erro ao salvar.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleResetCounters = async () => {
    if (!window.confirm('Zerar contadores agora? Não pode ser desfeito.')) return;
    try {
      await adminAgentsService.resetUserQuotaCounters(userId);
      showToast('Contadores zerados.', 'success');
      await refresh();
    } catch {
      showToast('Erro ao zerar.', 'error');
    }
  };

  if (loading) {
    return (
      <section className="border-t border-surface-border pt-6 mt-6" data-testid="quota-section">
        <p className="text-sm text-content-tertiary">Carregando cota…</p>
      </section>
    );
  }

  if (!quota || !form) return null;

  const catalogModels = Object.keys(quota.available_models_catalog ?? {});

  return (
    <section
      className="border-t border-surface-border pt-6 mt-6 space-y-4"
      data-testid="quota-section"
    >
      <h2 className="text-lg font-semibold text-content-primary">Cotas de IA</h2>

      <div className="flex border-b border-surface-border">
        <button
          type="button"
          onClick={() => setTab('edit')}
          data-testid="quota-tab-edit"
          className={`px-3 py-2 text-sm font-medium border-b-2 ${
            tab === 'edit'
              ? 'border-accent text-accent'
              : 'border-transparent text-content-tertiary hover:text-content-primary'
          }`}
        >
          Editar
        </button>
        <button
          type="button"
          onClick={() => setTab('audit')}
          data-testid="quota-tab-audit"
          className={`px-3 py-2 text-sm font-medium border-b-2 ${
            tab === 'audit'
              ? 'border-accent text-accent'
              : 'border-transparent text-content-tertiary hover:text-content-primary'
          }`}
        >
          Auditoria ({audit.length})
        </button>
      </div>

      {tab === 'edit' && (
        <div className="space-y-4">
          <div className="space-y-2" data-testid="quota-usage">
            <UsageBar
              label="Tokens hoje"
              used={quota.tokens_used_today}
              max={quota.max_tokens_per_day}
            />
            <UsageBar
              label="Tokens este mês"
              used={quota.tokens_used_this_month}
              max={quota.max_tokens_per_month}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <NumberInput
              label="Máx agentes pessoais"
              value={form.max_personal_agents}
              onChange={(v) => setForm({ ...form, max_personal_agents: v })}
              testId="input-max-agents"
            />
            <NumberInput
              label="Máx arquivos por agente"
              value={form.max_files_per_agent}
              onChange={(v) => setForm({ ...form, max_files_per_agent: v })}
              testId="input-max-files"
            />
            <NumberInput
              label="Máx MB por arquivo"
              value={form.max_file_size_mb}
              onChange={(v) => setForm({ ...form, max_file_size_mb: v })}
              testId="input-max-file-mb"
            />
            <NumberInput
              label="Tokens / dia"
              value={form.max_tokens_per_day}
              onChange={(v) => setForm({ ...form, max_tokens_per_day: v })}
              testId="input-tokens-day"
            />
            <NumberInput
              label="Tokens / mês"
              value={form.max_tokens_per_month}
              onChange={(v) => setForm({ ...form, max_tokens_per_month: v })}
              testId="input-tokens-month"
            />
          </div>

          <div>
            <span className="text-xs font-semibold text-content-primary block mb-1">
              Modelos permitidos
            </span>
            {catalogModels.length === 0 ? (
              <p className="text-xs text-content-tertiary">Catálogo indisponível.</p>
            ) : (
              <div className="flex flex-wrap gap-2" data-testid="models-multiselect">
                {catalogModels.map((m) => {
                  const checked = (form.allowed_models ?? []).includes(m);
                  return (
                    <label
                      key={m}
                      className={`px-3 py-1.5 rounded-lg border cursor-pointer text-sm select-none ${
                        checked
                          ? 'border-accent bg-accent/10 text-accent'
                          : 'border-surface-border text-content-tertiary hover:text-content-primary'
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="sr-only"
                        checked={checked}
                        onChange={() => {
                          const cur = new Set(form.allowed_models ?? []);
                          if (checked) cur.delete(m);
                          else cur.add(m);
                          setForm({ ...form, allowed_models: Array.from(cur) });
                        }}
                      />
                      {m}
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          <label className="block">
            <span className="text-xs font-semibold text-content-primary block mb-1">
              Motivo da alteração (opcional)
            </span>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="quota-reason"
              placeholder="Ex: Aprovado pelo gerente em ticket #123."
              className="w-full rounded-lg bg-surface-secondary border border-surface-border px-3 py-2 text-content-primary text-sm"
            />
            <p className="text-xs text-content-tertiary mt-1">
              Vai pro log de auditoria. Use pra contextualizar mudanças.
            </p>
          </label>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              data-testid="quota-save"
              className="bg-accent hover:bg-accent-hover text-accent-contrast font-medium px-4 py-2 rounded-lg disabled:opacity-50 transition-colors"
            >
              {saving ? 'Salvando…' : 'Salvar cota'}
            </button>
            <button
              type="button"
              onClick={handleResetCounters}
              data-testid="quota-reset"
              className="border border-surface-border text-content-primary px-4 py-2 rounded-lg hover:bg-surface-secondary"
            >
              Zerar contadores agora
            </button>
          </div>
        </div>
      )}

      {tab === 'audit' && (
        <ul className="space-y-2 text-sm" data-testid="audit-list">
          {audit.length === 0 ? (
            <p className="text-content-tertiary">Sem mudanças registradas.</p>
          ) : (
            audit.map((log) => (
              <li
                key={log.id}
                className="border-l-4 border-accent pl-3 py-1 rounded-r bg-surface-secondary"
              >
                <div className="flex items-baseline gap-2 flex-wrap">
                  <strong className="text-content-primary">
                    {log.admin_user_name ?? 'Sistema'}
                  </strong>
                  <span className="text-content-tertiary text-xs">mudou</span>
                  <code className="text-xs bg-surface-primary px-1.5 py-0.5 rounded text-accent">
                    {log.field_changed}
                  </code>
                  <span className="text-content-tertiary text-xs">de</span>
                  <code className="text-xs bg-surface-primary px-1.5 py-0.5 rounded">
                    {JSON.stringify(log.old_value)}
                  </code>
                  <span className="text-content-tertiary text-xs">pra</span>
                  <code className="text-xs bg-surface-primary px-1.5 py-0.5 rounded">
                    {JSON.stringify(log.new_value)}
                  </code>
                  <span className="text-content-tertiary text-xs ml-auto">
                    {fmtDate(log.created_at)}
                  </span>
                </div>
                {log.reason && (
                  <p className="text-content-tertiary italic text-xs mt-1">"{log.reason}"</p>
                )}
              </li>
            ))
          )}
        </ul>
      )}
    </section>
  );
};

export default UserQuotaSection;
